import { isEqual } from 'lodash-es';
import { alpha, Stack } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { tzdate, TZDate } from '@repo/tzdate';
import { useEffect, useState, type JSX } from 'react';
import { WarningMessage } from 'src/components/domain/booking-base/BookingCardWarningMessage';
import {
    ProductCardContainer,
    ProductCardHeader,
} from 'src/components/domain/product-card-2/ProductCard2';
import { ProductCardInfoContainer } from 'src/components/domain/product-card-2/ProductCardInfo';
import { useOnClickBook } from 'src/hooks/domain/activities/useOnClickBook';
import { MediaQueryAttributeInput } from '@repo/common-utils/mediaQueryAttributeInputHelper';
import {
    CustomizationsContext,
    useCustomizations,
} from 'src/components/utils/theme/customizations';
import { ThemeType } from 'src/components/utils/theme/ThemeType';
import { getProductMaxEntrants } from '@repo/widget-utils/activities/bilberryProductEntrants';
import { updateQuantityData } from '@repo/widget-utils/price-helper';
import ProductCardListContainer from '../product-list-2/product-card-list-2/ProductCardListContainer';
import ProductCardListItem from '../product-list-2/product-card-list-2/ProductCardListItem';
import { useAvailabilities, useProducts } from '@repo/widget-utils/services/hooks/product';
import { ActivityProductCardInfoContent } from 'src/components/domain/product-card-2/ActivityProductCard';
import { Product } from '@repo/types';
import { BookingBaseForm } from 'src/components/domain/booking-base/BookingBase';
import { useLocale } from '@repo/i18n';
import { getDateRangeVariant } from '../../activity-booking/booking/Booking';
import { InlineSummary } from 'src/components/domain/summary/InlineSummary';
import { useNativeBookingCoverage } from 'src/hooks/domain/native-membership/useNativeBookingCoverage';
import { ProductCard2Landscape } from 'src/components/domain/product-card-2/ProductCardLandscape';
import { selectIsProductInCart, cartAtom } from 'src/state/cart/cartAtom';
import { useAtomSelector } from 'src/hooks';
import {
    productDisablePaymentPlans,
    productInstancesHasDepositPrices,
} from '@repo/widget-utils/cart/cartUtils';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import useTicketTypesWithinProduct from 'src/hooks/domain/cart/useTicketTypesWithinProduct';
import usePriceQuantities from 'src/hooks/domain/cart/usePriceQuantities';
import useUpdateNewCartItem from 'src/hooks/domain/cart/useUpdateNewCartItem';
import { BookingContextProvider, useBookingContext } from 'src/widgets/BookingContext';
import {
    setSelectedTicketType,
    setQuantities,
    setDefaultTicketOptions,
} from 'src/state/booking/bookingReducer';

import InCartIndicator from 'src/components/domain/cart/InCartIndicator';
import useUpdateAvilabilitySearchPeriod from 'src/hooks/domain/cart/useUpdateAvilabilitySearchPeriod';
import { useActivityQuantities } from 'src/hooks/domain/booking/useActivityQuantities';

type PropsLandscape = {
    productCollectionId: string | null;
    productCatalogIds?: string[];
    productCatalogUrls?: string[];
    hideReadMore?: boolean;
    hideImageIfMissing?: boolean;
    cardBorder?: string;
    cardBorderRadius?: string;
    orientation: 'landscape';
    dateSelectionFirst?: boolean;
};

type PropsPortrait = Omit<PropsLandscape, 'orientation'> & {
    scroll: boolean;
    numElements: MediaQueryAttributeInput;
    backgroundColors: string[];
    textColors: string[];
    primaryColors: string[];
    primaryTextColors: string[];
    accentColors: string[];
    accentTextColors: string[];
    orientation?: 'portrait';
};

export function ProductListBookable(props: PropsPortrait | PropsLandscape): JSX.Element {
    if (props.orientation === 'landscape') {
        return <ProductListBookableLandscape {...props} />;
    }
    return <ProductListBookablePortrait {...props} />;
}

export function ProductListBookableLandscape(props: PropsLandscape): JSX.Element {
    const {
        productCollectionId,
        productCatalogIds,
        productCatalogUrls = [],
        hideImageIfMissing,
        cardBorder,
        cardBorderRadius,
        dateSelectionFirst = false,
    } = props;
    const { data: products } = useProducts({
        ids: productCatalogIds ?? [],
        collectionId: productCollectionId?.toString(),
    });

    const productCardList = products.map((product, i) => {
        return (
            <BookingContextProvider key={product.id} initialTicketOptions={[]}>
                <ProductCard2Landscape
                    product={product}
                    url={productCatalogUrls[i] ?? product.url ?? '#'}
                    body={
                        <ProductCardBookingForm
                            product={product}
                            dateSelectionFirst={dateSelectionFirst}
                        />
                    }
                    hideImage={hideImageIfMissing && !product.coverImage.src}
                    readMoreProductId={product.id}
                    readMoreLinkVariant={props.hideReadMore ? 'none' : 'href'}
                    hidePriceIcon
                    hideIcons={!!product.icons && product.icons.length === 0}
                    border={cardBorder}
                    borderRadius={cardBorderRadius}
                />
            </BookingContextProvider>
        );
    });

    return (
        <Stack gap={3} width="100%">
            {productCardList}
        </Stack>
    );
}

export function ProductListBookablePortrait(props: PropsPortrait) {
    const {
        scroll,
        numElements,
        accentColors,
        accentTextColors,
        backgroundColors,
        primaryColors,
        primaryTextColors,
        textColors,
        productCollectionId,
        productCatalogIds,
        productCatalogUrls = [],
        hideImageIfMissing,
        dateSelectionFirst = false,
    } = props;
    const customizations = useCustomizations();

    const { data: products } = useProducts({
        ids: productCatalogIds ?? [],
        collectionId: productCollectionId?.toString(),
    });

    const productCardList = products.map((product, i) => {
        const url = productCatalogUrls[i] ?? product.url ?? '#';
        const { length } = backgroundColors;
        const cur = i % length;
        const alternatingCustomizations: ThemeType = {
            ...customizations,
            productCardColor: backgroundColors[cur] ?? customizations.productCardColor,
            productCardTextColor: textColors[cur] ?? customizations.productCardTextColor,
            productCardPrimaryColor: primaryColors[cur] ?? customizations.productCardPrimaryColor,
            productCardPrimaryColorContrast:
                primaryTextColors[cur] ?? customizations.productCardPrimaryColorContrast,
            productCardAccentColor: accentColors[cur] ?? customizations.productCardAccentColor,
            productCardAccentColorContrast:
                accentTextColors[cur] ?? customizations.productCardAccentColorContrast,
            bookingWidgetColorContrast: textColors[cur] ?? customizations.productCardTextColor,
        };

        return (
            <BookingContextProvider key={product.id} initialTicketOptions={[]}>
                <CustomizationsContext.Provider value={alternatingCustomizations}>
                    <ProductCardListItem
                        scroll={scroll}
                        numElements={numElements}
                        numProductCatalogs={products.length}
                    >
                        <ProductCardContainer>
                            {!(hideImageIfMissing && !product.coverImage.src) && (
                                <ProductCardHeader imageUrl={product.coverImage.src} url={url} />
                            )}
                            <ProductCardInfoContainer>
                                <ActivityProductCardInfoContent
                                    hideFromLabel={hideImageIfMissing && !product.coverImage.src}
                                    product={product}
                                    url={url}
                                    readMoreLinkVariant={props.hideReadMore ? 'none' : 'href'}
                                    readMoreProductId={product.id}
                                />
                                <ProductCardBookingForm
                                    product={product}
                                    dateSelectionFirst={dateSelectionFirst}
                                />
                            </ProductCardInfoContainer>
                        </ProductCardContainer>
                    </ProductCardListItem>
                </CustomizationsContext.Provider>
            </BookingContextProvider>
        );
    });

    return (
        <ProductCardListContainer
            scroll={scroll}
            numElements={numElements}
            productListPadding={customizations.productListPadding}
        >
            {productCardList.length > 0 && productCardList}
        </ProductCardListContainer>
    );
}

function ProductCardBookingForm(props: { product: Product; dateSelectionFirst?: boolean }) {
    const { product, dateSelectionFirst } = props;
    const customizations = useCustomizations();
    const configuration = useConfigurations();
    const { t } = useLocale();

    const inCart = useAtomSelector(cartAtom, selectIsProductInCart, product.id);
    const {
        quantities,
        selectedProducts,
        selectedTicketType,
        isLoadingPrices,
        hasChosenDate,
        attemptedBooking,
        bookingDispatch,
    } = useBookingContext();

    // TODO: Refactor this so it is reused between Booking and ProductCardBookingForm

    const [dateRange, setDateRange] = useState<DateRange<TZDate>>([null, null]);

    const { nextProductAvailabilityStart, availabilitySearchPeriod, setAvailabilitySearchPeriod } =
        useUpdateAvilabilitySearchPeriod(product);

    const config = useConfigurations();
    const { data: availabilities, isLoading: isLoadingAvailabilities } = useAvailabilities(
        product,
        availabilitySearchPeriod.startDay,
        availabilitySearchPeriod.endDay,
        config.includeClosedAvailability,
    );

    const hasDepositPrices = selectedProducts
        ? productInstancesHasDepositPrices(selectedProducts)
        : false;
    const newItemDisablePaymentPlans = productDisablePaymentPlans(
        product,
        false,
        hasDepositPrices,
        configuration.disableMembershipBooking,
    );
    useUpdateNewCartItem(newItemDisablePaymentPlans);

    const ticketTypes = useTicketTypesWithinProduct(product);
    const priceQuantities = usePriceQuantities(product, selectedProducts, quantities);
    const { discountedTotal, isLoading: isCoverageLoading } = useNativeBookingCoverage();

    function noExtendSetDateRange(newRange: DateRange<TZDate>) {
        // Removes the ability to extend range in both directions in mui calendar
        if (dateRange[0]?.format('YYYY-MM-DD') !== newRange[0]?.format('YYYY-MM-DD')) {
            newRange[1] = null;
        }
        setDateRange(newRange);
    }

    useEffect(() => {
        const updatedQuantities = updateQuantityData(
            selectedProducts ?? [],
            product?.ticketOptions ?? [],
            quantities,
        );

        if (!isEqual(updatedQuantities, quantities)) {
            bookingDispatch(setQuantities(updatedQuantities));
        }

        if (ticketTypes.length === 1) {
            bookingDispatch(setSelectedTicketType(ticketTypes[0]));
        } else if (ticketTypes.length > 0) {
            bookingDispatch(
                setSelectedTicketType(
                    selectedTicketType
                        ? ticketTypes.find((t) => t.id === selectedTicketType.id)
                        : undefined,
                ),
            );
        }
    }, [selectedProducts, product, quantities, selectedTicketType, ticketTypes, bookingDispatch]);

    // Set default ticket options when product loads (for showing unavailable tickets)
    useEffect(() => {
        if (product?.ticketOptions) {
            bookingDispatch(setDefaultTicketOptions(product.ticketOptions));
        }
    }, [product?.ticketOptions, bookingDispatch]);

    const onClickBook = useOnClickBook(priceQuantities, product, setDateRange);

    // Use new domain hook for activity quantities
    const { inputItems, errors, onChangeQuantity, disabledMessage } = useActivityQuantities();

    // Split errors for BookingBaseForm compatibility
    const totalErrors = errors.filter((e) => e.type === 'total');

    // Calculate button state
    const disableBookButton = (!hasChosenDate && attemptedBooking) || errors.length > 0;

    // Activity-specific warning label logic
    function getWarningLabel() {
        if (!hasChosenDate) return undefined;
        if (errors.some((e) => e.error === 'minEntrants')) {
            return t.a_minimum_of_participants_is_required_to_book_this_product;
        }
        if (errors.some((e) => e.error === 'capacity')) {
            return t.no_available_capacity_for_this_tour;
        }
        return undefined;
    }
    const warningLabel = getWarningLabel();

    // if "god mode" is enabled, allow booking from one year ago
    // otherwise, booking can start from the next available date
    const minBookingDate = configuration.enableGodMode
        ? tzdate().add(-1, 'year')
        : nextProductAvailabilityStart;

    const productCapacity = getProductMaxEntrants(selectedProducts);

    return (
        <Stack alignItems="center" gap={2} textAlign="left" width="100%">
            <>
                <Stack gap={1} width="100%">
                    <BookingBaseForm
                        inputFieldBorder={`1px solid ${alpha(
                            customizations.productCardTextColor,
                            0.2,
                        )}`}
                        travelerQuantities={quantities}
                        availabilityData={availabilities}
                        isLoading={isLoadingAvailabilities}
                        availabilitySearchPeriod={availabilitySearchPeriod}
                        setAvailabilitySearchPeriod={setAvailabilitySearchPeriod}
                        totalErrors={totalErrors}
                        onSelectDateRange={noExtendSetDateRange}
                        dateRangeVariant={getDateRangeVariant(product)}
                        selectedDateRange={dateRange}
                        ticketTypes={ticketTypes}
                        variant="filled"
                        backgroundColor={customizations.productCardColor}
                        color={customizations.productCardTextColor}
                        labelColor={customizations.productCardTextColor}
                        id={product.id}
                        minDate={minBookingDate}
                        dateSelectionFirst={dateSelectionFirst}
                        quantityInputItems={inputItems}
                        onChangeQuantity={onChangeQuantity}
                        disabledMessage={disabledMessage}
                    />
                </Stack>
                <WarningMessage label={warningLabel} />

                <InlineSummary
                    onBook={onClickBook}
                    quantities={priceQuantities}
                    fromPrice={product.fromPrice}
                    bookingDisabled={disableBookButton}
                    hasCapacity={productCapacity !== 0}
                    totalPriceOverride={discountedTotal}
                    isLoadingPrices={isLoadingPrices || isCoverageLoading}
                    isWaitingList={
                        selectedProducts?.some(
                            (product) => product.hasWaitingList && product.isClosedForBooking,
                        ) ?? false
                    }
                />
                {inCart && <InCartIndicator />}
            </>
        </Stack>
    );
}
